// Window-function detection inside a select-item expression. Scans for a
// top-level `OVER` keyword and parses the `( … )` body into a WindowSpec.
// v1 keeps the frame opaque (full text + unit + range) — VQE-Edit composes
// frame edits as text and the Resolver does not validate them.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT,
// acceptance criterion `SelectItem.window`.

import type { ParseOptions } from '../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { IdentifierRef, SqlRange } from '../../sql-grammar/model';
import { readIdentifier, readQualifiedName, foldIdentifier } from '../scope/identifiers';
import type { OrderByItem, WindowSpec, WindowFrame, WindowFrameUnit } from '../types';

import { scanTopLevelKeywords, findTopLevelCommas, trimRange, skipParen } from './scanner';
import { skipContextFor } from '../classify/tokenSkip';
import type { SelectExtractCtx } from './extractQueryStructure.select';

export function extractWindowSpec(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): WindowSpec | undefined {
  const { source, options, lineIdx } = ctx;
  const kws = scanTopLevelKeywords(source, start, end, options);
  const overIdx = kws.findIndex((k) => k.upper === 'OVER');
  if (overIdx < 0) return undefined;
  const over = kws[overIdx];

  // Skip whitespace; expect `(` or a named window.
  let i = over.end;
  while (i < end && /\s/.test(source[i])) i++;
  if (source[i] !== '(') {
    // Named window: `OVER w` references a WINDOW clause.
    const id = readIdentifier(source, i, end, options);
    if (!id) return undefined;
    return {
      range: rangeFromOffsets(lineIdx, over.start, id.end),
      namedWindow: id.value,
    };
  }
  const body = parseWindowSpecBody(ctx, i, end);

  return {
    range: rangeFromOffsets(lineIdx, over.start, body.closeIdx),
    ...(body.partitionBy && body.partitionBy.length ? { partitionBy: body.partitionBy } : {}),
    ...(body.orderBy && body.orderBy.length ? { orderBy: body.orderBy } : {}),
    ...(body.frame ? { frame: body.frame } : {}),
  };
}

// Parsed fields of a window body `( … )` — the shared core behind both an
// inline `OVER (…)` spec and a `WINDOW w AS (…)` clause definition. `openParen`
// points AT the opening `(`; `closeIdx` is the index one past the matching `)`
// (or `end` when the paren is unclosed). Reused by the WINDOW-clause extractor
// (`extractQueryStructure.windowClause.ts`) so definition bodies are
// byte-identical to inline OVER bodies.
export type WindowSpecBody = {
  readonly partitionBy?: readonly IdentifierRef[];
  readonly orderBy?: readonly OrderByItem[];
  readonly frame?: WindowFrame;
  readonly closeIdx: number;
};

export function parseWindowSpecBody(
  ctx: SelectExtractCtx,
  openParen: number,
  end: number,
): WindowSpecBody {
  const { source, options } = ctx;
  const ctx2 = skipContextFor(options);
  const closeIdx = skipParen(source, openParen, end, ctx2);
  const innerStart = openParen + 1;
  const innerEnd = closeIdx > 0 ? closeIdx - 1 : end;
  const partitionBy = parseWindowPartition(ctx, innerStart, innerEnd);
  const orderBy = parseWindowOrderBy(ctx, innerStart, innerEnd);
  const frame = parseWindowFrame(ctx, innerStart, innerEnd);
  return {
    ...(partitionBy && partitionBy.length ? { partitionBy } : {}),
    ...(orderBy && orderBy.length ? { orderBy } : {}),
    ...(frame ? { frame } : {}),
    closeIdx,
  };
}

function parseWindowPartition(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly IdentifierRef[] | undefined {
  const { source, options, lineIdx } = ctx;
  const inner = scanTopLevelKeywords(source, start, end, options);
  const partIdx = inner.findIndex((k) => k.upper === 'PARTITION');
  if (partIdx < 0) return undefined;
  if (!inner[partIdx + 1] || inner[partIdx + 1].upper !== 'BY') return undefined;
  const region = boundedRegion(inner, partIdx + 2, ['ORDER', 'ROWS', 'RANGE', 'GROUPS'], inner[partIdx + 1].end, end);
  return parseRefList(source, options, lineIdx, region.start, region.end);
}

function parseWindowOrderBy(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly OrderByItem[] | undefined {
  const { source, options, lineIdx } = ctx;
  const inner = scanTopLevelKeywords(source, start, end, options);
  const ordIdx = inner.findIndex((k) => k.upper === 'ORDER');
  if (ordIdx < 0) return undefined;
  if (!inner[ordIdx + 1] || inner[ordIdx + 1].upper !== 'BY') return undefined;
  const region = boundedRegion(inner, ordIdx + 2, ['ROWS', 'RANGE', 'GROUPS'], inner[ordIdx + 1].end, end);
  const trimmed = trimRange(source, region.start, region.end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return undefined;
  const commas = findTopLevelCommas(source, trimmed.startOffset, trimmed.endOffset, options);
  const out: OrderByItem[] = [];
  let s = trimmed.startOffset;
  for (const c of commas) {
    out.push(buildOrderItem(source, options, lineIdx, s, c));
    s = c + 1;
  }
  out.push(buildOrderItem(source, options, lineIdx, s, trimmed.endOffset));
  return out;
}

function parseWindowFrame(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): WindowFrame | undefined {
  const { source, options, lineIdx } = ctx;
  const inner = scanTopLevelKeywords(source, start, end, options);
  for (let i = 0; i < inner.length; i++) {
    const u = inner[i].upper;
    if (u === 'ROWS' || u === 'RANGE' || u === 'GROUPS') {
      const unit: WindowFrameUnit = u === 'ROWS' ? 'rows' : u === 'RANGE' ? 'range' : 'groups';
      return {
        range: rangeFromOffsets(lineIdx, inner[i].start, end),
        unit,
        text: source.slice(inner[i].start, end).trim(),
      };
    }
  }
  return undefined;
}

function boundedRegion(
  kws: readonly { upper: string; start: number }[],
  fromIdx: number,
  stops: readonly string[],
  fallbackStart: number,
  fallbackEnd: number,
): { start: number; end: number } {
  const stopSet = new Set(stops);
  let endOffset = fallbackEnd;
  for (let k = fromIdx; k < kws.length; k++) {
    if (stopSet.has(kws[k].upper)) { endOffset = kws[k].start; break; }
  }
  return { start: fallbackStart, end: endOffset };
}

function parseRefList(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): IdentifierRef[] {
  const trimmed = trimRange(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return [];
  const commas = findTopLevelCommas(source, trimmed.startOffset, trimmed.endOffset, options);
  const out: IdentifierRef[] = [];
  let s = trimmed.startOffset;
  const push = (a: number, b: number): void => {
    const r = trimRange(source, a, b, options);
    const qn = readQualifiedName(source, r.startOffset, r.endOffset, options);
    if (!qn) {
      out.push({
        qualified: {
          name: source.slice(r.startOffset, r.endOffset).trim(), raw: source.slice(r.startOffset, r.endOffset),
          quoted: false, foldedName: foldIdentifier(source.slice(r.startOffset, r.endOffset).trim(), false, options),
        },
        range: rangeFromOffsets(lineIdx, r.startOffset, r.endOffset), kind: 'column',
      });
      return;
    }
    out.push({
      qualified: qn.qualified,
      range: rangeFromOffsets(lineIdx, r.startOffset, r.endOffset),
      kind: 'column',
    });
  };
  for (const c of commas) { push(s, c); s = c + 1; }
  push(s, trimmed.endOffset);
  return out;
}

function buildOrderItem(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): OrderByItem {
  void options;
  const trimmed = trimRange(source, start, end, options);
  const text = source.slice(trimmed.startOffset, trimmed.endOffset);
  const range: SqlRange = rangeFromOffsets(lineIdx, trimmed.startOffset, trimmed.endOffset);
  const m = text.match(/\s+(NULLS\s+(FIRST|LAST))\s*$/i);
  let nulls: 'first' | 'last' | 'default' | undefined;
  let textWithoutNulls = text;
  if (m) {
    nulls = m[2].toUpperCase() === 'FIRST' ? 'first' : 'last';
    textWithoutNulls = text.slice(0, m.index);
  }
  const dirMatch = textWithoutNulls.match(/\s+(ASC|DESC)\s*$/i);
  let direction: 'asc' | 'desc' | 'default' = 'default';
  let exprText = textWithoutNulls.trim();
  if (dirMatch) {
    direction = dirMatch[1].toUpperCase() === 'ASC' ? 'asc' : 'desc';
    exprText = textWithoutNulls.slice(0, dirMatch.index).trim();
  }
  return {
    range, expressionText: exprText, direction,
    ...(nulls !== undefined ? { nulls } : {}),
  };
}
